import { PERMISSIONS } from '@/constants/permissions'
import { hasToken } from '@/helpers/auth-token'

type Crumb = { label: string; to?: object }
const bc = (...crumbs: Crumb[]) => crumbs

const setTitle = (title: string) => `${title} | KYC Portal`

//Auth
const authRoutes = [
  {
    path: '/auth/sign-in',
    name: 'auth.sign-in',
    meta: { title: setTitle('Sign In') },
    component: () => import('@/views/auth/SignIn.vue'),
  },
  {
    path: '/auth/verify-sso',
    name: 'auth.verify-sso',
    meta: { title: setTitle('SSO Sign In') },
    component: () => import('@/views/auth/verify-sso.vue'),
  },
  {
    path: '/auth/forgot-password',
    name: 'auth.forgot-password',
    meta: { title: setTitle('Forgot Password') },
    component: () => import('@/views/auth/ForgotPassword.vue'),
  },
  {
    path: '/auth/reset-password',
    name: 'auth.reset-password',
    meta: { title: setTitle('Reset Password') },
    component: () => import('@/views/auth/ResetPass.vue'),
  },
]

//Errors
const errorRoutes = [
  {
    path: '/error/403',
    name: 'error.403',
    meta: { title: setTitle('Access Denied'), authRequired: true },
    component: () => import('@/views/auth/error-403.vue'),
  },
  {
    path: '/error/404',
    name: 'error.404',
    meta: { title: setTitle('Not Found') },
    component: () => import('@/views/auth/error-404.vue'),
  },
  {
    path: '/error/500',
    name: 'error.500',
    meta: { title: setTitle('Server Error') },
    component: () => import('@/views/auth/error-500.vue'),
  },
  { path: '/:catchAll(.*)', redirect: '/error/404' },
]

//Dashboard
export const dashboardRoutes = [
  {
    path: '/dashboard',
    name: 'dashboard.index',
    meta: {
      title: setTitle('Dashboard'),
      authRequired: true,
      breadcrumb: bc({ label: 'Dashboard' }),
    },
    component: () => import('@/views/dashboard/DashboardPage.vue'),
  },
  {
    path: '/profile/:id',
    name: 'profile.view',
    meta: {
      title: setTitle('Profile'),
      authRequired: true,
      breadcrumb: bc({ label: 'Dashboard', to: { name: 'dashboard.index' } }, { label: 'Profile' }),
    },
    component: () => import('@/views/profile/ProfilePage.vue'),
  },
]

//Applications
const applicationRoutes = [
  {
    path: '/applications',
    name: 'applications.index',
    meta: {
      title: setTitle('Applications'),
      authRequired: true,
      permission: PERMISSIONS.APPLICATIONS_VIEW,
      breadcrumb: bc({ label: 'Applications' }),
    },
    component: () => import('@/views/applications/ApplicationsListPage.vue'),
  },
  {
    path: '/applications/upload',
    name: 'applications.upload',
    meta: {
      title: setTitle('Upload Statement'),
      authRequired: true,
      permission: PERMISSIONS.APPLICATIONS_UPLOAD,
      breadcrumb: bc(
        { label: 'Applications', to: { name: 'applications.index' } },
        { label: 'Upload Statement' }
      ),
    },
    component: () => import('@/views/applications/StatementUploadPage.vue'),
  },
  {
    path: '/applications/history',
    name: 'applications.history',
    meta: {
      title: setTitle('Upload History'),
      authRequired: true,
      permission: PERMISSIONS.APPLICATIONS_VIEW,
      breadcrumb: bc(
        { label: 'Applications', to: { name: 'applications.index' } },
        { label: 'Upload History' }
      ),
    },
    component: () => import('@/views/applications/UploadHistoryPage.vue'),
  },
  {
    path: '/applications/:id',
    name: 'applications.show',
    meta: {
      title: setTitle('Statement Detail'),
      authRequired: true,
      permission: PERMISSIONS.APPLICATIONS_VIEW,
      breadcrumb: bc(
        { label: 'Applications', to: { name: 'applications.index' } },
        { label: 'Statement Detail' }
      ),
    },
    component: () => import('@/views/applications/StatementDetailPage.vue'),
  },
]

//Customers
const customerRoutes = [
  {
    path: '/customers',
    name: 'customers.index',
    meta: {
      title: setTitle('Customers'),
      authRequired: true,
      permission: PERMISSIONS.CUSTOMERS_VIEW,
      breadcrumb: bc({ label: 'Customers' }),
    },
    component: () => import('@/views/customers/CustomersListPage.vue'),
  },
  {
    path: '/customers/creditScore',
    name: 'customer.creditScore',
    meta: {
      title: setTitle('customer CreditScore'),
      authRequired: true,
      permission: PERMISSIONS.CUSTOMERS_VIEW,
      breadcrumb: bc(
        { label: 'Customers', to: { name: 'customers.index' } },
        { label: 'Customer Detail' }
      ),
    },
    component: () => import('@/views/customers/CreditScore.vue'),
  },

  {
    path: '/customers/:id',
    name: 'customers.show',
    meta: {
      title: setTitle('Customer Detail'),
      authRequired: true,
      permission: PERMISSIONS.CUSTOMERS_VIEW,
      breadcrumb: bc(
        { label: 'Customers', to: { name: 'customers.index' } },
        { label: 'Customer Detail' }
      ),
    },
    component: () => import('@/views/customers/CustomerDetailPage.vue'),
  }
]

//Reports
const reportRoutes = [
  {
    path: '/reports',
    name: 'reports.index',
    meta: {
      title: setTitle('Reports'),
      authRequired: true,
      permission: PERMISSIONS.REPORTS_VIEW,
      breadcrumb: bc({ label: 'Reports' }),
    },
    component: () => import('@/views/reports/ReportsListPage.vue'),
  },
  {
    path: '/reports/:id',
    name: 'reports.show',
    meta: {
      title: setTitle('Report Detail'),
      authRequired: true,
      permission: PERMISSIONS.REPORTS_VIEW,
      breadcrumb: bc(
        { label: 'Reports', to: { name: 'reports.index' } },
        { label: 'Report Detail' }
      ),
    },
    component: () => import('@/views/reports/ReportDetailPage.vue'),
  },
  {
    path: '/credit-score',
    name: 'reports.credit-score',
    meta: {
      title: setTitle('Credit Score Reports'),
      authRequired: true,
      permission: PERMISSIONS.REPORTS_VIEW,
      breadcrumb: bc(
        { label: 'Reports', to: { name: 'reports.index' } },
        { label: 'Credit Score Reports' }
      ),
    },
    component: () => import('@/views/reports/CreditScore.vue'),
  },
  {
    path: '/credit-details/:customerId',
    name: 'reports.credit-details',
    meta: {
      title: setTitle('Credit Details'),
      authRequired: true,
      permission: PERMISSIONS.REPORTS_VIEW,
      breadcrumb: bc(
        { label: 'Reports', to: { name: 'reports.index' } },
        { label: 'Credit Score Reports', to: { name: 'reports.credit-score' } },
        { label: 'Credit Details' }
      ),
    },
    component: () => import('@/views/reports/CreditDetails.vue'),
  },
]

//Admin
const adminRoutes = [
  {
    path: '/users',
    name: 'users.index',
    meta: {
      title: setTitle('Users'),
      authRequired: true,
      permission: PERMISSIONS.USERS_MANAGE,
      breadcrumb: bc({ label: 'Administration' }, { label: 'Users' }),
    },
    component: () => import('@/views/users/UsersListPage.vue'),
  },
  {
    path: '/users/roles',
    name: 'users.roles',
    meta: {
      title: setTitle('Roles & Permissions'),
      authRequired: true,
      permission: PERMISSIONS.ROLES_MANAGE,
      breadcrumb: bc({ label: 'Administration' }, { label: 'Roles & Permissions' }),
    },
    component: () => import('@/views/users/RolesPage.vue'),
  },
  {
    path: '/audit-log',
    name: 'audit.index',
    meta: {
      title: setTitle('Audit Log'),
      authRequired: true,
      permission: PERMISSIONS.AUDIT_VIEW,
      breadcrumb: bc({ label: 'Administration' }, { label: 'Audit Log' }),
    },
    component: () => import('@/views/users/AuditLogPage.vue'),
  },
  {
    path: '/audit-log/:id',
    name: 'audit.show',
    meta: {
      title: setTitle('Audit Log Detail'),
      authRequired: true,
      permission: PERMISSIONS.AUDIT_VIEW,
      breadcrumb: bc(
        { label: 'Administration' },
        { label: 'Audit Log', to: { name: 'audit.index' } },
        { label: 'Event Detail' },
      ),
    },
    component: () => import('@/views/users/AuditLogDetailPage.vue'),
  },
]

//Settings
const settingsRoutes = [
  {
    path: '/settings',
    redirect: { name: 'settings.ratio-configuration' },
  },
  {
    path: '/settings/ratio-configuration',
    name: 'settings.ratio-configuration',
    meta: {
      title: setTitle('Ratio Configuration'),
      authRequired: true,
      permission: PERMISSIONS.SETTINGS_MANAGE,
      breadcrumb: bc({ label: 'Settings' }, { label: 'Ratio Configuration' }),
    },
    component: () => import('@/views/settings/RatioConfigurationPage.vue'),
  },
]

//Root
const rootRoute = {
  path: '/',
  name: 'root',
  redirect: () => (hasToken() ? { name: 'dashboard.index' } : { name: 'auth.sign-in' }),
}

export const allRoute = [
  rootRoute,
  ...authRoutes,
  ...dashboardRoutes,
  ...applicationRoutes,
  ...customerRoutes,
  ...reportRoutes,
  ...adminRoutes,
  ...settingsRoutes,
  ...errorRoutes,
]
